import AddIcon from '@mui/icons-material/Add'
import CommentIcon from '@mui/icons-material/Comment'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

const EvaluacionQuestion = ({
  question,
  codigo_capitulo,
  onChange = () => {},
  onAutoSave = () => {},
}) => {
  const [response, setResponse] = useState({
    valor: question.valor,
    porcentaje: question.porcentaje,
  })
  const [observaciones, setObservaciones] = useState(question.observaciones)
  const [tipo] = useState(
    question.tipo === 'S' || (question.tipo === 'V' && question.campo === 'V')
      ? 'V'
      : question.tipo === 'V' && question.campo !== 'V'
        ? 'P'
        : 'O',
  )
  const [showObs, setShowObs] = useState(!!question.observaciones)

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const isMountedRef = useRef(false)
  const obsTimerRef = useRef(null)

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }
    onChange({
      codigo_capitulo: codigo_capitulo,
      codigo_aspecto: question.codigo_aspecto,
      valor: response.valor,
      porcentaje: response.porcentaje,
      observaciones,
    })
  }, [response, observaciones])

  const handleChange = (e) => {
    const rawValue = e.target.value
    const value = rawValue === '' ? null : !isNaN(Number(rawValue)) ? Number(rawValue) : rawValue
    const newResponse = {
      valor: tipo === 'V' ? value : response.valor,
      porcentaje: tipo === 'P' ? value : response.porcentaje,
    }
    setResponse(newResponse)
    onAutoSave({
      codigo_capitulo,
      codigo_aspecto: question.codigo_aspecto,
      valor: newResponse.valor,
      porcentaje: newResponse.porcentaje,
      observaciones,
    })
  }

  const handleSliderChange = (_e, newValue) => {
    setResponse((prev) => ({ ...prev, porcentaje: newValue }))
    onAutoSave({
      codigo_capitulo,
      codigo_aspecto: question.codigo_aspecto,
      valor: response.valor,
      porcentaje: newValue,
      observaciones,
    })
  }

  const handleIncrement = () => {
    const newValor = (Number(response.valor) || 0) + 1
    setResponse((prev) => ({ ...prev, valor: newValor }))
    onAutoSave({
      codigo_capitulo,
      codigo_aspecto: question.codigo_aspecto,
      valor: newValor,
      porcentaje: response.porcentaje,
      observaciones,
    })
  }

  const handleDecrement = () => {
    const newValor = (Number(response.valor) || 0) - 1
    setResponse((prev) => ({ ...prev, valor: newValor }))
    onAutoSave({
      codigo_capitulo,
      codigo_aspecto: question.codigo_aspecto,
      valor: newValor,
      porcentaje: response.porcentaje,
      observaciones,
    })
  }

  const handleChangeObservaciones = (e) => {
    const value = e.target.value
    setObservaciones(value)
    if (obsTimerRef.current) clearTimeout(obsTimerRef.current)
    obsTimerRef.current = setTimeout(() => {
      onAutoSave({
        codigo_capitulo,
        codigo_aspecto: question.codigo_aspecto,
        valor: response.valor,
        porcentaje: response.porcentaje,
        observaciones: value,
      })
    }, 800)
  }

  if (isDesktop) {
    return (
      <>
        <Box
          id={`question-${question.codigo_aspecto}`}
          sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}
        >
          {/* Izquierda: título + detalle */}
          <Box sx={{ flex: '0 0 40%' }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {question.titulo_aspecto}
            </Typography>
            {question.detalle_aspecto && (
              <Typography variant="body2" color="text.secondary">
                {question.detalle_aspecto}
              </Typography>
            )}
          </Box>

          {/* Derecha: inputs */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Fila: [input flex:1] + [botón comentarios] */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Input ocupa todo el espacio disponible */}
              <Box sx={{ flex: 1 }}>
                {question.tipo === 'S' && (
                  <RadioGroup
                    row
                    value={response.valor != null ? String(response.valor) : ''}
                    onChange={handleChange}
                    sx={{ mt: 1, justifyContent: 'space-between' }}
                  >
                    {question.respuestas?.map((option) => (
                      <FormControlLabel
                        key={option.valor}
                        value={String(option.valor)}
                        control={<Radio />}
                        label={option.description}
                        disabled={question.cerrado === 'S'}
                      />
                    ))}
                  </RadioGroup>
                )}

                {question.tipo === 'T' && (
                  <TextField
                    multiline
                    rows={3}
                    placeholder="Introduzca aquí su respuesta"
                    value={observaciones ?? ''}
                    onChange={handleChangeObservaciones}
                    fullWidth
                    margin="normal"
                    disabled={question.cerrado === 'S'}
                  />
                )}

                {question.tipo === 'V' &&
                  (question.campo === 'V' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                      <IconButton
                        onClick={handleDecrement}
                        disabled={question.cerrado === 'S'}
                        size="small"
                        color="primary"
                      >
                        <RemoveIcon />
                      </IconButton>
                      <TextField
                        type="number"
                        value={response.valor ?? ''}
                        onChange={handleChange}
                        sx={{ width: 100 }}
                        inputProps={{ style: { textAlign: 'center' } }}
                        disabled={question.cerrado === 'S'}
                      />
                      <IconButton
                        onClick={handleIncrement}
                        disabled={question.cerrado === 'S'}
                        size="small"
                        color="primary"
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    (() => {
                      const sliderMarks = question.respuestas.map((r) => ({
                        value: r.valor,
                        label: String(r.description),
                      }))
                      const sliderMin = question.respuestas.length
                        ? Math.min(...question.respuestas.map((r) => r.valor))
                        : 0
                      const sliderMax = question.respuestas.length
                        ? Math.max(...question.respuestas.map((r) => r.valor))
                        : 100
                      return (
                        <Slider
                          value={response.porcentaje ?? sliderMin}
                          onChange={handleSliderChange}
                          valueLabelDisplay="auto"
                          step={null}
                          marks={sliderMarks}
                          min={sliderMin}
                          max={sliderMax}
                          disabled={question.cerrado === 'S'}
                        />
                      )
                    })()
                  ))}
              </Box>

              {/* Botón comentarios — solo para tipo !== 'O' (S y V) */}
              {tipo !== 'O' && (
                <Tooltip title="Añadir comentarios / observaciones">
                  <IconButton
                    size="small"
                    color={showObs ? 'primary' : 'default'}
                    onClick={() => setShowObs((v) => !v)}
                  >
                    <CommentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Observaciones — fila independiente debajo */}
            {tipo !== 'O' && showObs && (
              <TextField
                label="Observaciones"
                value={observaciones ?? ''}
                onChange={handleChangeObservaciones}
                fullWidth
                margin="normal"
                disabled={question.cerrado === 'S'}
              />
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
      </>
    )
  }

  return (
    <Box key={question.codigo_aspecto} id={`question-${question.codigo_aspecto}`}>
      <Typography variant="h6">{question.titulo_aspecto}</Typography>
      {question.detalle_aspecto ? (
        <Typography variant="body2">{question.detalle_aspecto}</Typography>
      ) : (
        <></>
      )}
      {question.tipo === 'S' &&
        (isDesktop ? (
          <RadioGroup
            row
            value={response.valor != null ? String(response.valor) : ''}
            onChange={handleChange}
            sx={{ mt: 1 }}
          >
            {question.respuestas?.map((option) => (
              <FormControlLabel
                key={option.valor}
                value={String(option.valor)}
                control={<Radio />}
                label={option.description}
                disabled={question.cerrado === 'S'}
              />
            ))}
          </RadioGroup>
        ) : (
          <TextField
            select
            value={response.valor != null ? String(response.valor) : ''}
            onChange={handleChange}
            fullWidth
            margin="normal"
            disabled={question.cerrado === 'S'}
          >
            <MenuItem value="" disabled>
              <em>Seleccionar...</em>
            </MenuItem>
            {question.respuestas?.map((option) => (
              <MenuItem key={option.valor} value={String(option.valor)}>
                {option.description}
              </MenuItem>
            ))}
          </TextField>
        ))}
      {question.tipo === 'T' && (
        <TextField
          multiline
          rows={3}
          placeholder="Introduzca aquí su respuesta"
          value={observaciones ?? ''}
          onChange={handleChangeObservaciones}
          fullWidth
          margin="normal"
          disabled={question.cerrado === 'S'}
        />
      )}
      {question.tipo === 'V' &&
        (question.campo === 'V' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <IconButton
              onClick={handleDecrement}
              disabled={question.cerrado === 'S'}
              size="small"
              color="primary"
            >
              <RemoveIcon />
            </IconButton>
            <TextField
              type="number"
              value={response.valor ?? ''}
              onChange={handleChange}
              sx={{ width: 100 }}
              inputProps={{ style: { textAlign: 'center' } }}
              disabled={question.cerrado === 'S'}
            />
            <IconButton
              onClick={handleIncrement}
              disabled={question.cerrado === 'S'}
              size="small"
              color="primary"
            >
              <AddIcon />
            </IconButton>
          </Box>
        ) : (
          (() => {
            const sliderMarks = question.respuestas.map((r) => ({
              value: r.valor,
              label: String(r.description),
            }))
            const sliderMin = question.respuestas.length
              ? Math.min(...question.respuestas.map((r) => r.valor))
              : 0
            const sliderMax = question.respuestas.length
              ? Math.max(...question.respuestas.map((r) => r.valor))
              : 100
            return (
              <Slider
                value={response.porcentaje ?? sliderMin}
                onChange={handleSliderChange}
                valueLabelDisplay="auto"
                step={null}
                marks={sliderMarks}
                min={sliderMin}
                max={sliderMax}
                disabled={question.cerrado === 'S'}
              />
            )
          })()
        ))}
      {question.tipo !== 'T' && (
        <TextField
          label="Observaciones"
          value={observaciones ?? ''}
          onChange={handleChangeObservaciones}
          fullWidth
          margin="normal"
          disabled={question.cerrado === 'S'}
        />
      )}
      <Divider sx={{ my: 4 }} />
    </Box>
  )
}

export default EvaluacionQuestion
